import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Box, Typography, Button } from "@mui/material";

const STORAGE_KEY = "cmTrendTourSeenV1";
const POPOVER_W = 340;

function getRect(id) {
  const el = document.getElementById(id);
  return el ? el.getBoundingClientRect() : null;
}

/**
 * Lightweight, hand-built first-time walkthrough for the Conversation
 * Monitoring screen — a highlighted outline around each step's target
 * element (found by DOM id, not threaded refs, since the targets live across
 * three unrelated component subtrees: TrendsView, TrendChart's legend,
 * WhatChangedSection, TopicBucketBreakdown), with the rest of the page dimmed
 * via a single huge-spread box-shadow on that same highlight box — no
 * separate overlay element needed — plus a small popover pointing at it.
 *
 * Manual-start only for now: exposes `start()` via ref so the caller can
 * render its own "Take a tour" trigger wherever makes sense on the page,
 * without this component needing to own that UI. Auto-starting on a user's
 * first visit (localStorage-gated) is deliberately held back until the
 * client has seen the tour and confirmed they actually want it to appear
 * automatically — re-adding that is a small, self-contained change (a single
 * useEffect checking STORAGE_KEY before calling setStepIndex(0)), not a
 * redesign, once that's decided. The "seen" flag is still written on finish/
 * skip below so that auto-start, once reintroduced, respects anyone who
 * already went through the tour manually in the meantime.
 *
 * Deliberately not a third-party tour library (react-joyride etc.) — this
 * app doesn't already have one, and four simple highlighted steps don't
 * justify a new dependency, consistent with building the trend chart itself
 * on plain SVG/MUI rather than pulling in Recharts.
 */
const OnboardingTour = forwardRef(function OnboardingTour({ steps }, ref) {
  const [stepIndex, setStepIndex] = useState(null); // null = not running
  const [, bumpTick] = useState(0); // forces a re-render to recompute the target's rect

  useImperativeHandle(ref, () => ({
    start: () => setStepIndex(0),
  }));

  useEffect(() => {
    if (stepIndex === null) return undefined;
    const el = document.getElementById(steps[stepIndex].id);
    if (el) el.scrollIntoView({ behavior: "auto", block: "center" });
    const onReflow = () => bumpTick((n) => n + 1);
    window.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);
    return () => {
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    };
  }, [stepIndex, steps]);

  if (stepIndex === null) return null;
  const step = steps[stepIndex];
  const rect = getRect(step.id);
  if (!rect) return null; // target not mounted this frame (e.g. still loading) — skip rendering rather than highlight nothing

  const isLast = stepIndex === steps.length - 1;

  const finish = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setStepIndex(null);
  };
  const next = () => (isLast ? finish() : setStepIndex((i) => i + 1));
  const back = () => setStepIndex((i) => Math.max(0, i - 1));

  // Popover placement: below the target by default, flipped above when
  // there isn't room below; clamped horizontally so it never runs off-screen.
  // When the target itself is taller than most of the viewport (e.g. the
  // whole trend-chart card), there's no good spot "adjacent" to it on either
  // side — dock the popover to a fixed viewport corner instead of trying to
  // squeeze it next to the highlight, which otherwise gets clipped off-screen.
  const targetTooTall = rect.height > window.innerHeight * 0.6;
  const spaceBelow = window.innerHeight - rect.bottom;
  const placeAbove = !targetTooTall && spaceBelow < 200 && rect.top > 200;
  const popoverLeft = targetTooTall
    ? window.innerWidth - POPOVER_W - 24
    : Math.min(Math.max(rect.left, 16), window.innerWidth - POPOVER_W - 16);

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          top: rect.top - 6,
          left: rect.left - 6,
          width: rect.width + 12,
          height: rect.height + 12,
          border: "2px solid #1976d2",
          borderRadius: "10px",
          // Thin blue halo right at the edge, then a huge-spread dark shadow
          // that fills the rest of the viewport (box-shadow follows the
          // box's own border-radius, so the "cutout" stays rounded to match).
          boxShadow: "0 0 0 4px rgba(25,118,210,0.15), 0 0 0 9999px rgba(16,24,40,0.55)",
          pointerEvents: "none",
          zIndex: 1300,
        }}
      />

      <Box
        sx={{
          position: "fixed",
          ...(targetTooTall
            ? { bottom: 24 }
            : placeAbove
              ? { bottom: window.innerHeight - rect.top + 12 }
              : { top: rect.bottom + 12 }),
          left: popoverLeft,
          width: POPOVER_W,
          // Dark, same as TrendChart.js's own crosshair tooltip (#1a2027 +
          // white text) — rather than a white/light card, which blended into
          // the page's own white cards with no backdrop dimming to separate
          // them. Reuses an already-established pattern in this same feature
          // instead of introducing a third visual treatment.
          bgcolor: "#1a2027",
          borderRadius: "10px",
          boxShadow: "0 8px 28px rgba(16,24,40,0.35)",
          p: "16px 18px",
          zIndex: 1301,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#5b9bf5", letterSpacing: "0.03em" }}>
            STEP {stepIndex + 1} OF {steps.length}
          </Typography>
          <Typography onClick={finish} sx={{ fontSize: 11.5, color: "#98a2b3", cursor: "pointer", userSelect: "none", "&:hover": { color: "#d0d5dd" } }}>
            Skip tour
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: "#fff", mb: 0.5 }}>{step.title}</Typography>
        <Typography sx={{ fontSize: 13, lineHeight: 1.55, color: "#d0d5dd", mb: 2 }}>{step.body}</Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          {stepIndex > 0 && (
            <Button size="small" onClick={back} sx={{ textTransform: "none", color: "#d0d5dd" }}>
              Back
            </Button>
          )}
          <Button size="small" variant="contained" onClick={next} sx={{ textTransform: "none", boxShadow: "none" }}>
            {isLast ? "Got it" : "Next"}
          </Button>
        </Box>
      </Box>
    </>
  );
});

export default OnboardingTour;
